import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, PartyPopper, UtensilsCrossed, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { IMG } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];
const SERVICE_ICONS = [Building2, PartyPopper, UtensilsCrossed];
const PKG_IMG = [IMG.catBreakfast, IMG.catLunch, IMG.fingerFood];

const Reveal = ({ children, delay = 0, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    {...rest}
  >
    {children}
  </motion.div>
);

export default function CateringPage({ t }) {
  const c = t.cateringPage;
  const navigate = useNavigate();

  return (
    <main data-testid="catering-page">
      <Seo title="Catering — Kurkuma AI Kitchen" siteName="Kurkuma AI Kitchen" description={c.heroSub} />

      <section className="relative overflow-hidden bg-navy text-cream" data-testid="catering-hero">
        <img src={IMG.catCorporate} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <h1 className="font-display text-4xl font-bold tracking-tight md:text-5xl" data-testid="catering-heading">{c.heroTitle}</h1>
            <p className="mt-2 text-cream/70">{c.heroSub}</p>
          </motion.div>
        </div>
      </section>

      {/* services */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-8" data-testid="catering-services">
        <Reveal className="mb-10">
          <h2 className="font-display text-3xl font-bold text-navy">{c.servicesTitle}</h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {c.services.map((s, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <Reveal key={s.t} delay={i * 0.08} data-testid={`catering-service-${i}`}>
                <div className="h-full rounded-2xl bg-paper p-7 shadow-sm transition-shadow hover:shadow-lg">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/15 text-branddark">
                    <Icon className="h-6 w-6" />
                  </span>
                  <p className="mt-4 font-display text-lg font-semibold text-navy">{s.t}</p>
                  <p className="mt-1 text-sm text-ink/55">{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* packages */}
      <section className="bg-paper py-14" data-testid="catering-packages">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <Reveal className="mb-10">
            <h2 className="font-display text-3xl font-bold text-navy">{c.packagesTitle}</h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {c.packages.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.08} data-testid={`catering-package-${i}`}>
                <div className="group overflow-hidden rounded-2xl bg-cream shadow-sm transition-shadow hover:shadow-lg">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={PKG_IMG[i]} alt={p.t} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-5">
                    <p className="font-display text-base font-semibold text-navy">{p.t}</p>
                    <p className="mt-1 text-sm text-ink/55">{p.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* buffet CTA */}
      <section className="mx-auto max-w-7xl px-4 py-14 md:px-8" data-testid="catering-buffet">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-navy p-10 text-cream md:flex-row md:items-center">
            <div>
              <h2 className="font-display text-3xl font-bold">{c.buffet.t}</h2>
              <p className="mt-1 text-cream/60">{c.buffet.d}</p>
            </div>
            <button
              onClick={() => navigate("/contact")}
              data-testid="buffet-quote-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-3.5 text-sm font-semibold text-navy transition-colors hover:bg-branddark"
            >
              {c.buffet.cta}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
